/**
 * Lo que se abre sobre la fila de un modelo: sus memorias.
 *
 * La fila de arriba es «iPhone 17 Pro Max». Adentro está lo que de verdad se
 * compra: cuánta memoria, a cuánto, cuántas hay y en qué colores. Es el orden
 * en que se pregunta en el mostrador —«¿el 17 Pro Max? ¿en qué memoria?»—.
 *
 * LAS MEMORIAS SON LAS DEL LINEUP, NO LAS CARGADAS
 * ------------------------------------------------
 * Igual que la lista de modelos de afuera. Antes acá se listaba lo que había
 * en la base, así que desplegar el 18 Pro Max mostraba un solo renglón —el de
 * 256 GB— y el 512 y el 1 TB no aparecían por ningún lado: no estaban ocultos,
 * nunca se habían creado. El hueco más caro del catálogo era el único que no
 * se veía.
 *
 * Ahora el renglón existe igual y dice que está sin cargar. Y como lo que
 * falta es la plata, la plata se carga ahí mismo: costo y precio de venta en
 * el renglón, sin abrir la ficha.
 *
 * EL DESPLEGABLE USA LAS COLUMNAS DE LA TABLA
 * -------------------------------------------
 * Esto es lo que lo hacía ilegible. Los renglones se dibujaban adentro de UNA
 * celda que cruzaba la tabla entera, con su propio orden inventado: la
 * capacidad, después los dos importes pegados, después el stock y al final los
 * colores. El resultado era que debajo del encabezado COLOR había un precio,
 * debajo de COSTO había un «3 u.» y el cuadradito del color caía en la mitad
 * de la fila. Cada nivel decía las mismas cosas en distinto orden, y por eso
 * no se entendía qué se estaba mirando.
 *
 * Ahora cada memoria es UNA FILA DE VERDAD de la misma tabla, con las mismas
 * columnas: el costo cae abajo de COSTO y el color abajo de COLOR. La sangría
 * del título es lo único que dice que está un nivel más adentro, que es
 * exactamente lo que hay que decir.
 *
 * Y el modelo dejó de mostrar plata. Mostraba un rango —«desde tanto hasta
 * tanto»— que resumía lo que las memorias dicen exacto dos líneas más abajo, y
 * encima competía por la mirada con los números de verdad. El precio es de la
 * memoria: es lo que cambia cuando cambia la memoria.
 */
import { useState, type MouseEvent, type ReactNode } from "react";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { formatearNumero, type Moneda } from "@tora/shared";

import { MuestraColor } from "@/components/catalogo/MuestraColor";
import { nombreCompleto } from "@/components/catalogo/nombre";
import { UnidadesDelModelo } from "@/components/catalogo/UnidadesDelModelo";
import type { ExtrasDeFila } from "@/components/catalogo/ListaProductos";
import { Importe, InputImporte } from "@/components/money";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { TableCell, TableCellImporte, TableFilaDetalle, TableRow } from "@/components/ui/table";
import { aNumero, type Producto } from "@/lib/tipos";

import { etiquetaCapacidad } from "./logica";
import {
  coloresDelRenglon,
  preciosDeRenglon,
  type PreciosDeMemoria,
  type RenglonDeMemoria,
} from "./renglones";

/** Guardar la plata de una memoria. La pantalla decide si crea o actualiza. */
export type GuardarPrecios = (
  renglon: RenglonDeMemoria,
  precios: PreciosDeMemoria,
) => Promise<void> | void;

export interface MemoriasDelModeloProps {
  renglones: ReadonlyArray<RenglonDeMemoria>;
  unidades: ReadonlyMap<number, Producto[]>;
  onEditar: (p: Producto) => void;
  /** Lo que la sección le agrega a cada equipo: chips, pendientes, acciones. */
  extras?: ExtrasDeFila;
  /** Cargar costo y precio sin abrir la ficha. Sin esto, el renglón es de lectura. */
  onGuardarPrecios?: GuardarPrecios;
  /** Marcar «no trabajo esta memoria». */
  onAlternarOculta?: (clave: string) => void;
  /** Para la cotización del selector de moneda del editor. */
  cotizacion?: number;
}

/**
 * Las columnas propias de la sección, para UN renglón.
 *
 * Es la misma forma que usa la fila del modelo: una celda por columna, en el
 * mismo orden, y vacío donde esa columna no aplica. En iPhone la única es
 * COLOR y la contesta con los colores que HAY de esa memoria; en Usados la
 * segunda es BATERIA, que es de un equipo y no de un renglón de precios, así
 * que queda vacía.
 */
export type ColumnasDeRenglon = (renglon: RenglonDeMemoria) => ReadonlyArray<ReactNode>;

export interface PropsDeFilas extends MemoriasDelModeloProps {
  /** Cuántas columnas propias tiene la sección, para dibujar todas las celdas. */
  cantidadColumnas: number;
  columnas?: ColumnasDeRenglon;
  /** El ancho de la tabla, para la fila del editor y la de los equipos. */
  colSpan: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// La tabla
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las memorias como filas de la misma tabla.
 *
 * Las que no se trabajan van al fondo y plegadas, igual que los modelos
 * apagados: no se borran —no habría de dónde volver a prenderlas— pero tampoco
 * pueden estar mezcladas, porque de un modelo que solo se pide en 256 y 512
 * serían dos renglones muertos arriba de los dos que importan.
 */
export function FilasDeMemoria(props: PropsDeFilas): JSX.Element {
  const [verApagadas, setVerApagadas] = useState(false);
  const trabajadas = props.renglones.filter((r) => !r.oculta);
  const apagadas = props.renglones.filter((r) => r.oculta);

  return (
    <>
      {trabajadas.map((r) => (
        <FilaMemoria key={r.clave} renglon={r} {...props} />
      ))}
      {apagadas.length > 0 ? (
        <>
          <TableRow>
            <TableCell colSpan={props.colSpan}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-expanded={verApagadas}
                onClick={() => setVerApagadas((v) => !v)}
                className="w-full justify-start pl-8 text-muted-foreground"
              >
                {apagadas.length === 1
                  ? "1 memoria que no trabajo"
                  : `${apagadas.length} memorias que no trabajo`}
              </Button>
            </TableCell>
          </TableRow>
          {verApagadas
            ? apagadas.map((r) => <FilaMemoria key={r.clave} renglon={r} {...props} />)
            : null}
        </>
      ) : null}
    </>
  );
}

function FilaMemoria({
  renglon,
  unidades,
  onEditar,
  extras,
  onGuardarPrecios,
  onAlternarOculta,
  cotizacion,
  cantidadColumnas,
  columnas,
  colSpan,
}: { renglon: RenglonDeMemoria } & PropsDeFilas): JSX.Element {
  const [editando, setEditando] = useState(false);
  const etiqueta = etiquetaCapacidad(renglon.memoriaGb) || "Sin memoria";
  /**
   * La ficha que recibe los precios. La primera: un renglón con dos es un
   * duplicado que hay que resolver a mano, y mientras tanto la plata se le
   * carga a una sola —pisar las dos con el mismo número taparía el problema—.
   */
  const ficha = renglon.fichas[0] ?? null;
  const propias = columnas?.(renglon) ?? [];
  const equipos = [...renglon.fichas.flatMap((f) => unidades.get(f.id) ?? []), ...renglon.sueltos];
  // Sin lista de precios pero con equipos: cada uno tiene su precio abajo.
  const porEquipo = !ficha && renglon.sueltos.length > 0;

  if (editando && onGuardarPrecios) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan}>
          <EditorDePrecios
            renglon={renglon}
            etiqueta={etiqueta}
            cotizacion={cotizacion}
            onCancelar={() => setEditando(false)}
            onGuardar={async (precios) => {
              await onGuardarPrecios(renglon, precios);
              setEditando(false);
            }}
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow className={renglon.oculta ? "opacity-50" : undefined}>
        {/* La sangría es lo único que dice que está un nivel más adentro. */}
        <TableCell className="pl-8 text-sm tabular-nums">
          {etiqueta}
          {ficha?.es_usado ? <MarcaReferencia /> : null}
        </TableCell>
        {Array.from({ length: cantidadColumnas }, (_, i) => (
          <TableCell key={i} className="whitespace-nowrap text-sm">
            <span className="flex items-center gap-1">{propias[i]}</span>
          </TableCell>
        ))}
        <TableCellImporte>
          <CeldaPlata
            monto={ficha ? aNumero(ficha.costo) : 0}
            moneda={ficha?.costo_moneda ?? "USD"}
            que="el costo"
            etiqueta={etiqueta}
            porEquipo={porEquipo}
            onEditar={onGuardarPrecios ? () => setEditando(true) : undefined}
          />
        </TableCellImporte>
        <TableCellImporte>
          <CeldaPlata
            monto={ficha ? aNumero(ficha.precio_venta) : 0}
            moneda={ficha?.precio_moneda ?? "USD"}
            que="el precio de venta"
            etiqueta={etiqueta}
            porEquipo={porEquipo}
            onEditar={onGuardarPrecios ? () => setEditando(true) : undefined}
          />
        </TableCellImporte>
        <TableCellImporte>
          {/* Cero no se escribe: una columna con veinte "0 u." no deja ver
              los tres renglones que sí tienen algo. */}
          {renglon.stock > 0 ? (
            `${formatearNumero(renglon.stock, 0)} u.`
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCellImporte>
        <TableCell>
          <div className="flex items-center justify-end">
            {ficha ? (
              <IconButton
                icono={Pencil}
                tamano="sm"
                // El nombre ARMADO, no el guardado: dos memorias iguales de
                // distinto color se llaman igual, y dos botones con el mismo
                // nombre accesible no se pueden distinguir.
                aria-label={`Editar ${nombreCompleto(ficha)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditar(ficha);
                }}
              />
            ) : null}
            {onAlternarOculta ? (
              <OjoDeMemoria
                renglon={renglon}
                etiqueta={etiqueta}
                onAlternarOculta={onAlternarOculta}
              />
            ) : null}
          </div>
        </TableCell>
      </TableRow>

      {/* Los duplicados, para que se vean y se puedan arreglar. */}
      {renglon.fichas.slice(1).map((f) => (
        <TableRow key={f.id}>
          <TableCell colSpan={colSpan} className="pl-8 text-xs">
            <Button
              type="button"
              variant="link"
              className="text-warning"
              onClick={() => onEditar(f)}
            >
              Hay otra ficha de esta memoria: {nombreCompleto(f)}
            </Button>
          </TableCell>
        </TableRow>
      ))}

      {/**
       * Los equipos de esa memoria. Van en una fila que cruza la tabla y no en
       * columnas, a propósito: un equipo no es un renglón de precios —tiene
       * IMEI, batería, una seña— y meterlo en las mismas columnas diría que es
       * lo mismo un nivel más abajo, que es justo lo que no es.
       */}
      {equipos.length > 0 ? (
        <TableFilaDetalle colSpan={colSpan}>
          <div className="pl-8">
            <UnidadesDelModelo unidades={equipos} onEditar={onEditar} vacio="" extras={extras} />
          </div>
        </TableFilaDetalle>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las tarjetas del teléfono
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo mismo sin columnas.
 *
 * La tarjeta no tiene tabla que respetar: todo va en una línea, con la
 * capacidad adelante porque es lo que se busca al recorrer. Es el mismo
 * criterio que la tarjeta del modelo, que tampoco intenta ser una tabla
 * angosta.
 */
export function MemoriasDelModelo({
  renglones,
  unidades,
  onEditar,
  extras,
  onGuardarPrecios,
  onAlternarOculta,
  cotizacion,
}: MemoriasDelModeloProps): JSX.Element {
  const [verApagadas, setVerApagadas] = useState(false);
  const trabajadas = renglones.filter((r) => !r.oculta);
  const apagadas = renglones.filter((r) => r.oculta);
  const comun = { unidades, onEditar, extras, onGuardarPrecios, onAlternarOculta, cotizacion };

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {trabajadas.map((r) => (
          <ItemMemoria key={r.clave} renglon={r} {...comun} />
        ))}
      </ul>
      {apagadas.length > 0 ? (
        <div>
          <Button
            type="button"
            variant="dashed"
            size="sm"
            aria-expanded={verApagadas}
            onClick={(e) => {
              e.stopPropagation();
              setVerApagadas((v) => !v);
            }}
            className="justify-start"
          >
            {apagadas.length === 1
              ? "1 memoria que no trabajo"
              : `${apagadas.length} memorias que no trabajo`}
          </Button>
          {verApagadas ? (
            <ul className="mt-2 space-y-2 opacity-60">
              {apagadas.map((r) => (
                <ItemMemoria key={r.clave} renglon={r} {...comun} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ItemMemoria({
  renglon,
  unidades,
  onEditar,
  extras,
  onGuardarPrecios,
  onAlternarOculta,
  cotizacion,
}: { renglon: RenglonDeMemoria } & Omit<MemoriasDelModeloProps, "renglones">): JSX.Element {
  const [editando, setEditando] = useState(false);
  const etiqueta = etiquetaCapacidad(renglon.memoriaGb) || "Sin memoria";
  const ficha = renglon.fichas[0] ?? null;
  const equipos = [...renglon.fichas.flatMap((f) => unidades.get(f.id) ?? []), ...renglon.sueltos];
  // Sin lista de precios pero con equipos: cada uno tiene su precio abajo.
  const porEquipo = !ficha && renglon.sueltos.length > 0;

  if (editando && onGuardarPrecios) {
    return (
      <li>
        <EditorDePrecios
          renglon={renglon}
          etiqueta={etiqueta}
          cotizacion={cotizacion}
          onCancelar={() => setEditando(false)}
          onGuardar={async (precios) => {
            await onGuardarPrecios(renglon, precios);
            setEditando(false);
          }}
        />
      </li>
    );
  }

  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-16 shrink-0 text-xs font-medium tabular-nums">{etiqueta}</span>
        {ficha?.es_usado ? <MarcaReferencia /> : null}
        <CeldaPlata
          monto={ficha ? aNumero(ficha.costo) : 0}
          moneda={ficha?.costo_moneda ?? "USD"}
          que="el costo"
          etiqueta={etiqueta}
          porEquipo={porEquipo}
          onEditar={onGuardarPrecios ? () => setEditando(true) : undefined}
        />
        <CeldaPlata
          monto={ficha ? aNumero(ficha.precio_venta) : 0}
          moneda={ficha?.precio_moneda ?? "USD"}
          que="el precio de venta"
          etiqueta={etiqueta}
          porEquipo={porEquipo}
          onEditar={onGuardarPrecios ? () => setEditando(true) : undefined}
        />
        {renglon.stock > 0 ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatearNumero(renglon.stock, 0)} u.
          </span>
        ) : null}
        <span className="flex min-w-0 flex-1 items-center gap-1">
          {coloresDelRenglon(renglon, unidades).map((c) => (
            <MuestraColor key={c.id} color={c.color} />
          ))}
        </span>
        {ficha ? (
          <IconButton
            icono={Pencil}
            tamano="sm"
            className="shrink-0"
            aria-label={`Editar ${nombreCompleto(ficha)}`}
            onClick={(e) => {
              e.stopPropagation();
              onEditar(ficha);
            }}
          />
        ) : null}
        {onAlternarOculta ? (
          <OjoDeMemoria renglon={renglon} etiqueta={etiqueta} onAlternarOculta={onAlternarOculta} />
        ) : null}
      </div>
      {equipos.length > 0 ? (
        <div className="pl-16">
          <UnidadesDelModelo unidades={equipos} onEditar={onEditar} vacio="" extras={extras} />
        </div>
      ) : null}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las piezas que comparten las dos formas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un número de plata, o que falta.
 *
 * Un blanco parece que la columna no aplica; «Falta» dice que hay algo que
 * hacer, y apretarlo es justamente hacerlo. Sin forma de guardar —la lista de
 * solo lectura— el mismo texto va sin botón: sigue informando, pero no promete
 * una acción que no existe.
 */
function CeldaPlata({
  monto,
  moneda,
  que,
  etiqueta,
  porEquipo = false,
  onEditar,
}: {
  monto: number;
  moneda: Moneda;
  /** "el costo" | "el precio de venta", para el nombre accesible del botón. */
  que: string;
  /** La capacidad, que es lo que distingue este botón de los otros tres. */
  etiqueta: string;
  /**
   * No hay lista de precios pero sí equipos, cada uno con el suyo. «Falta»
   * mentiría: el equipo se puede vender. Se puede cargar igual el precio del
   * modelo, y ahí los equipos pasan a colgar de él.
   */
  porEquipo?: boolean;
  onEditar?: () => void;
}): JSX.Element {
  const contenido =
    monto > 0 ? (
      <Importe monto={monto} moneda={moneda} tamano="sm" />
    ) : porEquipo ? (
      <span className="text-xs text-muted-foreground">—</span>
    ) : (
      <span className="text-xs text-warning">Falta</span>
    );

  if (!onEditar) return contenido;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={`${monto > 0 ? "Cambiar" : "Cargar"} ${que} de ${etiqueta}`}
      className="px-2 font-normal"
      onClick={(e) => {
        e.stopPropagation();
        onEditar();
      }}
    >
      {contenido}
    </Button>
  );
}

/**
 * En Usados, el costo y el precio del renglón son los del precio de referencia
 * del modelo, no los de ningún equipo. Cada equipo tiene el suyo abajo, y sin
 * esta marca se leía como si el renglón fuera un teléfono más.
 */
function MarcaReferencia(): JSX.Element {
  return (
    <span
      className="ml-2 text-xs text-muted-foreground"
      title="Cuánto vale este modelo usado. No es un teléfono: cada equipo tiene su precio abajo."
    >
      precio de referencia
    </span>
  );
}

function OjoDeMemoria({
  renglon,
  etiqueta,
  onAlternarOculta,
}: {
  renglon: RenglonDeMemoria;
  etiqueta: string;
  onAlternarOculta: (clave: string) => void;
}): JSX.Element {
  return (
    <IconButton
      icono={renglon.oculta ? Eye : EyeOff}
      tamano="sm"
      className="shrink-0"
      aria-label={renglon.oculta ? `Volver a trabajar ${etiqueta}` : `No trabajo ${etiqueta}`}
      title={
        renglon.oculta
          ? "Volver a trabajarla: sube con las demás"
          : "No la trabajo: baja al fondo, sin borrar nada"
      }
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onAlternarOculta(renglon.clave);
      }}
    />
  );
}

/**
 * Los dos números del renglón, ahí mismo.
 *
 * EL PRECIO SE ESCRIBE: se carga el número que se cobra, y la ficha guarda el
 * margen que lo da (pendientes, decisión 4). Sale ese precio; si después
 * sube el costo, acompaña. Congelarlo es «Fijar este precio», en la ficha.
 */
function EditorDePrecios({
  renglon,
  etiqueta,
  cotizacion,
  onGuardar,
  onCancelar,
}: {
  renglon: RenglonDeMemoria;
  etiqueta: string;
  cotizacion?: number;
  onGuardar: (precios: PreciosDeMemoria) => Promise<void> | void;
  onCancelar: () => void;
}): JSX.Element {
  const [valores, setValores] = useState<PreciosDeMemoria>(() =>
    preciosDeRenglon(renglon.fichas[0] ?? null, renglon.hermana),
  );
  const [guardando, setGuardando] = useState(false);

  const cambiar = (parcial: Partial<PreciosDeMemoria>): void =>
    setValores((v) => ({ ...v, ...parcial }));

  /*
   * Lo que se escribe REEMPLAZA el número que había. Antes el campo no
   * mostraba el número entero (se leía «US$ 1») y el clic dejaba el cursor al
   * final: escribir 1790 sobre 1.290,00 grabó 17.901.290,00 en un iPhone
   * publicado. Ahora los campos tienen ancho para el número y, al entrar,
   * queda todo seleccionado.
   */
  const ficha = renglon.fichas[0] ?? null;
  // Solo se compara en la misma moneda: US$ 1.290 contra $ 1.500.000 no es un salto.
  const anterior =
    ficha && ficha.precio_moneda === valores.precioMoneda ? aNumero(ficha.precio_venta) : null;
  const saltoGrande =
    anterior !== null &&
    anterior > 0 &&
    valores.precio !== null &&
    valores.precio > 0 &&
    (valores.precio > anterior * 3 || valores.precio * 3 < anterior);

  return (
    <div
      className="flex flex-wrap items-start gap-3 rounded-md border border-border bg-muted p-2"
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => {
        if (e.target instanceof HTMLInputElement && e.target.inputMode === "decimal") {
          e.target.select();
        }
      }}
    >
      <span className="w-16 shrink-0 pt-2 text-xs font-medium tabular-nums">{etiqueta}</span>
      <InputImporte
        label="Costo"
        valor={valores.costo}
        onValorChange={(costo) => cambiar({ costo })}
        moneda={valores.costoMoneda}
        onMonedaChange={(costoMoneda: Moneda) => cambiar({ costoMoneda })}
        cotizacion={cotizacion}
        autoFocus
        className="w-72"
      />
      <InputImporte
        label="Precio de venta"
        valor={valores.precio}
        onValorChange={(precio) => cambiar({ precio })}
        moneda={valores.precioMoneda}
        onMonedaChange={(precioMoneda: Moneda) => cambiar({ precioMoneda })}
        cotizacion={cotizacion}
        className="w-72"
      />
      <div className="flex items-center gap-2 pt-6">
        <Button
          type="button"
          size="sm"
          cargando={guardando}
          onClick={async () => {
            setGuardando(true);
            try {
              await onGuardar(valores);
            } finally {
              setGuardando(false);
            }
          }}
        >
          Guardar
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
      {saltoGrande ? (
        <p className="w-full text-xs font-medium text-warning" role="status">
          Ojo: el precio nuevo es más de tres veces distinto del que tenía. Revisalo antes de
          guardar.
        </p>
      ) : null}
      {/* Sin costo no hay margen que despejar: el precio se guarda fijo. */}
      {valores.precio !== null && !(valores.costo !== null && valores.costo > 0) ? (
        <p className="w-full text-xs text-muted-foreground">
          Sin costo el precio queda fijo hasta que lo cambies.
        </p>
      ) : null}
    </div>
  );
}
